'use client';

import { useMemo } from 'react';
import { stegaClean } from '@sanity/client/stega';
import type { ITradingCompetitionPopup } from '@/sanity/lib/definitions';
import { useCampaignLive } from '@/hooks/useCampaignLive';

/**
 * Client-side live check for the Trading Competition pop-up campaign
 * (sanity/globals/TradingCompetitionPopup.ts).
 *
 * Also used by the homepage to stand the YouTube Live widget and the payout
 * toast down while the pop-up is up, so they never share the corner.
 *
 * QA/preview override: `?competitionPreview=1` forces the pop-up on before the
 * start date, `?competitionPreview=0` hides it while live.
 */

/** Same one-minute cadence as the other campaigns; the countdown ticks on its own. */
const CHECK_INTERVAL_MS = 60_000;

export function useTradingCompetitionPopupLive(config: ITradingCompetitionPopup | null | undefined): {
  config: ITradingCompetitionPopup | null;
  live: boolean;
} {
  // Draft/Presentation mode fetches with stega enabled — invisible characters
  // inside the dates would break Date parsing.
  const cleaned = useMemo(
    () => (stegaClean(config ?? null) ?? null) as ITradingCompetitionPopup | null,
    [config],
  );
  const live = useCampaignLive(cleaned, 'competitionPreview', CHECK_INTERVAL_MS);
  return { config: cleaned, live };
}
